import { Button } from '@heroui/react'
import { FormikControl } from '@qse/ui'
import type { FormikHelpers } from 'formik'
import { Form, Formik } from 'formik'
import { ArrowLeft } from 'lucide-react'
import { useEffect, useState } from 'react'
import { Link, useLocation, useNavigate } from 'react-router-dom'

import { useForgotPassword } from '../logic'

interface VerificationCodeData {
  confirmationCode: string
}

const VerificationCodeScreen: React.FC = () => {
  const location = useLocation()
  const navigate = useNavigate()
  const [isResending, setIsResending] = useState(false)
  const { handleVerifyCode, handleResendCode, verificationCodeValidationSchema } =
    useForgotPassword()

  const email = location.state?.email

  useEffect(() => {
    if (!email) {
      navigate('/auth/forgot-password', { replace: true })
    }
  }, [email, navigate])

  const handleResend = async () => {
    try {
      setIsResending(true)
      await handleResendCode(email)
    } catch (error) {
      console.error('Failed to resend code:', error)
    } finally {
      setIsResending(false)
    }
  }

  // Handle verification code submission
  const handleSubmit = async (
    values: VerificationCodeData,
    formikHelpers: FormikHelpers<VerificationCodeData>,
  ) => {
    try {
      formikHelpers.setStatus('')

      await handleVerifyCode(email, values, formikHelpers)
    } catch (error: any) {
      formikHelpers.setStatus('Invalid verification code. Please try again.')
    } finally {
      formikHelpers.setSubmitting(false)
    }
  }

  return (
    <div className="h-full">
      <div className="fixed top-10 left-10">
        <Button
          as={Link}
          className="font-medium"
          startContent={<ArrowLeft size={16} />}
          to="/auth/forgot-password"
          variant="light"
        >
          Back
        </Button>
      </div>

      <div className="flex items-center h-full w-full lg:pl-[110px]">
        <div className="lg:w-[440px] w-full p-8 lg:p-0 flex flex-col gap-11">
          <div className="flex flex-col gap-2">
            <h1 className="text-[28px] font-medium">Enter Verification Code</h1>
            <p className="text-[15px]">We sent a code to your email.</p>
            <button
              className="text-[#4793EB] text-start text-sm mt-10"
              disabled={isResending}
              onClick={handleResend}
            >
              Resend Code
            </button>
          </div>

          <Formik
            validateOnBlur={true}
            validateOnChange={true}
            validateOnMount={true}
            validationSchema={verificationCodeValidationSchema}
            initialValues={{
              confirmationCode: '',
            }}
            onSubmit={handleSubmit}
          >
            {({ isSubmitting, isValid, dirty }) => (
              <Form>
                <div className="flex justify-center items-center flex-col gap-[30px]">
                  <FormikControl
                    isRequired
                    control="otp"
                    label="Verification Code"
                    length={6}
                    name="confirmationCode"
                    size="lg"
                    classNames={{
                      segmentWrapper: 'gap-5',
                    }}
                  />
                </div>

                <Button
                  className="w-full mt-6 font-medium"
                  color="primary"
                  isDisabled={!isValid || !dirty}
                  isLoading={isSubmitting}
                  radius="sm"
                  type="submit"
                >
                  Verify Code
                </Button>

                <div className="text-center mt-6">
                  <span className="text-sm">
                    Remember your password?{' '}
                    <Link className="text-primary font-medium hover:underline" to="/auth/login">
                      Back to Login
                    </Link>
                  </span>
                </div>
              </Form>
            )}
          </Formik>
        </div>
      </div>
    </div>
  )
}

export default VerificationCodeScreen
